import React, { FC, ReactElement, useState } from 'react';
import { StatusReason, SupplierValidationRecordStatus } from '@mortee/domain/validationSystem';
import ClickEventPropagationBlocker from '@app/components/ClickEventPropagationBlocker';
import NakedDropdown from '@app/components/inputs/NakedDropdown';
import DropdownItem from '@app/components/inputs/DropdownItem';
import BigModal from '@app/components/BigModal';
import { BodySmallStartTransparentBlack600, Bold18TransparentBlack900 } from '@app/components/Text';
import styled from '@emotion/styled';
import Button from '@app/components/Button';
import MultiChipSelector, { MultiChipOption } from '@app/components/MultiChipSelector';
import TransitionLoader from '@app/components/TransitionLoader';
import { observer } from 'mobx-react';
import useAppStores from '@app/hooks/useAppStores';
import MorteeMode from '@mortee/morteeMode';

interface Props {
  status: SupplierValidationRecordStatus;
  allowMultiple: boolean;
  initialValues: StatusReason[];
  title: string;
  explanation: string;
  className?: string;
  onSelect(reasons: StatusReason[]): void;
  onNotSelect(): void;
}

const StatusReasonSelector: FC<Props> = observer((props) => {
  const { validationSystemStore } = useAppStores<MorteeMode>();

  const { status, allowMultiple, initialValues, title, explanation, className, onSelect, onNotSelect } = props;

  const reasons =
    status === SupplierValidationRecordStatus.clarificationExternal
      ? validationSystemStore.clarificationExternalReasons
      : validationSystemStore.cancelReasons;

  const [selectedReasonIds, setSelectedReasonIds] = useState<string[]>(initialValues.map((reason) => reason.id));

  function handleSubmit(): void {
    onSelect(reasons.result?.filter((reason) => selectedReasonIds.includes(reason.id)) ?? []);
  }

  return (
    <ClickEventPropagationBlocker className={className}>
      <SelectReasonModal
        closeFunc={onNotSelect}
        title={<Bold18TransparentBlack900.div>{title}</Bold18TransparentBlack900.div>}
        dataTestId='status-reason-modal'
      >
        <SelectExplanationText>{explanation}</SelectExplanationText>
        <TransitionLoader loading={reasons.isInProgress()}>
          {allowMultiple ? (
            <MultiChipSelector
              id='select-status-reasons'
              dataTestId='select-status-reasons'
              accessibilityLabel={title}
              options={(reasons.result ?? []).map((reason): MultiChipOption<string> => ({
                id: reason.id,
                value: reason.id,
                text: reason.reason,
              }))}
              value={selectedReasonIds}
              onChange={(newValue): void => setSelectedReasonIds(newValue ?? [])}
            />
          ) : (
            <NakedDropdown
              name='drp-select-status-reason'
              dataTestId='drp-select-status-reason'
              accessibilityLabel={title}
              placeholder='Reason'
              isSearchable
              disablePortal
              value={selectedReasonIds[0]}
              onChange={(newValue): void => setSelectedReasonIds(newValue ? [newValue] : [])}
            >
              {
                reasons.result?.map((option) => (
                  <DropdownItem key={option.id} value={option.id} textWhenSelected={option.reason}>
                    {option.reason}
                  </DropdownItem>
                )) as ReactElement[]
              }
            </NakedDropdown>
          )}
          <FormActions>
            <Button
              id='btn-status-reason-update-status'
              onClick={handleSubmit}
              colorScheme='primary'
              disabled={!reasons.isResolved() || selectedReasonIds.length === 0}
            >
              UPDATE STATUS
            </Button>
          </FormActions>
        </TransitionLoader>
      </SelectReasonModal>
    </ClickEventPropagationBlocker>
  );
});

export default StatusReasonSelector;

const SelectReasonModal = styled(BigModal)`
  padding: 16px;
  width: 500px;
  box-shadow: var(--box-shadow-3);

  --block-padding: 0;
  --side-padding: 0;
`;

const SelectExplanationText = styled(BodySmallStartTransparentBlack600.div)`
  margin-bottom: 8px;
`;

const FormActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-top: 16px;
`;
